import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Moment } from 'moment';

import { useAppSelector } from '../../../../../../hooks';
import { calculateTotalAmountByCurrencyAndAmount } from '../../../../../../helper';
import { getPenaltyWaiverState } from '../../../../../../store/V2/PenaltyWaiverSlice';
import PaidTutorAmountBreakDownModal from '../modals/ViewPenaltyWaiverModal/tutorPayments/PaidTutorAmountBreakDownModal';
import PendingTutorAmountBreakDownModal from '../modals/ViewPenaltyWaiverModal/tutorPayments/PendingTutorAmountBreakDownModal';

interface TutorPaymentDetailsCardProps {
  startDate: Moment | null;
  endDate: Moment | null;
}

const containerStyles: SxProps = {
  border: '1px solid #F1F5F9',
  borderRadius: '16px',
  width: 'fit-content',
};

const PaidAmountCard = ({
  total,
  onClick,
}: {
  total: string;
  onClick: () => void;
}) => {
  return (
    <Card elevation={0}>
      <CardContent style={{ padding: '0' }}>
        <Stack direction="row" alignItems="center" gap="20px">
          <img
            src={`${process.env.PUBLIC_URL}/images/tutorPayment/tutor-payment-paid-amount-icon.svg`}
            alt="tutor-amount-paid-icon"
            width="56px"
            height="56px"
          />
          <Stack>
            <Typography fontSize="16px" fontWeight={500} color="#1E293B">
              Total Paid Amount
            </Typography>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {total}
            </Typography>
          </Stack>
          <ButtonBase
            onClick={onClick}
            sx={{
              backgroundColor: '#919EAB14',
              color: '#1E293B',
              borderRadius: '8px',
              p: '10px 12px',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            View Breakdown
          </ButtonBase>
        </Stack>
      </CardContent>
    </Card>
  );
};

const PayableAmountCard = ({
  total,
  onClick,
}: {
  total: string;
  onClick: () => void;
}) => {
  return (
    <Card elevation={0}>
      <CardContent style={{ padding: '0' }}>
        <Stack direction="row" alignItems="center" gap="20px">
          <img
            src={`${process.env.PUBLIC_URL}/images/tutorPayment/tutor-payment-payable-amount-icon.svg`}
            alt="tutor-amount-paid-icon"
            width="56px"
            height="56px"
          />
          <Stack>
            <Typography fontSize="16px" fontWeight={500} color="#1E293B">
              Total Payable Amount
            </Typography>
            <Typography fontSize="14px" fontWeight={500} color="#1E293B">
              {total}
            </Typography>
          </Stack>
          <ButtonBase
            onClick={onClick}
            sx={{
              backgroundColor: '#919EAB14',
              color: '#1E293B',
              borderRadius: '8px',
              p: '10px 12px',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            View Breakdown
          </ButtonBase>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TutorPaymentDetailsCard: FunctionComponent<TutorPaymentDetailsCardProps> = ({
  startDate,
  endDate,
}) => {
  // Store
  const penaltyWaiversState = useAppSelector(getPenaltyWaiverState);

  // State
  const [paidBreakdownModalOpen, setPaidBreakdownModalOpen] = useState(false);
  const [payableBreakdownModalOpen, setPayableBreakdownModalOpen] = useState(
    false
  );

  const tutorAmountData =
    penaltyWaiversState.penaltyWaiverListData &&
    penaltyWaiversState.penaltyWaiverListData.tutorAmountData
      ? penaltyWaiversState.penaltyWaiverListData.tutorAmountData
      : undefined;

  let totalPaidAmount = '00';
  let totalPendingAmount = '00';

  if (tutorAmountData) {
    totalPaidAmount =
      calculateTotalAmountByCurrencyAndAmount(tutorAmountData.paidAmountData) ||
      '00';

    totalPendingAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingAmountData
      ) || '00';
  }

  const togglePaidBreakdownModalOpen = () => {
    setPaidBreakdownModalOpen((prevState) => !prevState);
  };

  const togglePayableBreakdownModalOpen = () => {
    setPayableBreakdownModalOpen((prevState) => !prevState);
  };

  return (
    <div style={{ padding: '0 20px 20px 20px' }}>
      <Card elevation={0} sx={containerStyles}>
        <CardContent style={{ padding: '16px' }}>
          <div>
            <Stack
              rowGap="20px"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <PaidAmountCard
                total={totalPaidAmount}
                onClick={togglePaidBreakdownModalOpen}
              />
              <Box
                sx={{
                  minHeight: '56px',
                  borderLeft: '1px dashed #CBD5E1',
                  margin: '0 30px',
                  display: { xs: 'none', sm: 'block' },
                }}
              />
              <PayableAmountCard
                total={totalPendingAmount}
                onClick={togglePayableBreakdownModalOpen}
              />
            </Stack>
          </div>
        </CardContent>
      </Card>
      {/* Modals */}
      {paidBreakdownModalOpen ? (
        <PaidTutorAmountBreakDownModal
          open={paidBreakdownModalOpen}
          onClose={togglePaidBreakdownModalOpen}
          startDate={startDate}
          endDate={endDate}
        />
      ) : null}
      {payableBreakdownModalOpen ? (
        <PendingTutorAmountBreakDownModal
          open={payableBreakdownModalOpen}
          onClose={togglePayableBreakdownModalOpen}
          startDate={startDate}
          endDate={endDate}
        />
      ) : null}
    </div>
  );
};

export default TutorPaymentDetailsCard;
